import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldAlert,
  ArrowLeft,
  Eye,
  RefreshCcw,
  Loader2,
  Eraser,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { PageBand } from "../components/layout/PageShell";

const ADMIN_EMAIL = "dvdquintana32@gmail.com";

const impersonateLabel = (row, currentUserId, impersonatingId) => {
  if (impersonatingId === row.user_id) return "Switching…";
  if (row.user_id === currentUserId) return "You";
  return "Impersonate";
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-AU", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const fmtDateShort = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-AU", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
};

export default function Admin() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState(null);
  const [error, setError] = useState(null);
  const [impersonatingId, setImpersonatingId] = useState(null);
  const [reseedOpen, setReseedOpen] = useState(false);
  const [reseeding, setReseeding] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetConfirm, setResetConfirm] = useState("");
  const [resetPreview, setResetPreview] = useState(null);

  const isAdmin =
    user?.is_admin === true ||
    (user?.email && user.email.trim().toLowerCase() === ADMIN_EMAIL);

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const { data } = await api.get("/admin/users");
        setUsers(data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Failed to load users");
      }
    })();
  }, [isAdmin]);

  const handleImpersonate = async (targetUser) => {
    setImpersonatingId(targetUser.user_id);
    try {
      await api.post(`/admin/impersonate/${targetUser.user_id}`);
      toast.success(`Now viewing as ${targetUser.name || targetUser.email}`);
      await checkAuth();
      navigate("/dashboard", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to impersonate user");
      setImpersonatingId(null);
    }
  };

  const handleReseedChloe = async () => {
    setReseeding(true);
    try {
      const { data } = await api.post("/admin/reseed-chloe");
      const wiped = data?.sessions_wiped ?? 0;
      const inserted = data?.sessions_inserted ?? 0;
      const created = data?.client_created;
      toast.success(
        created
          ? `Chloe created with ${inserted} sessions.`
          : `Chloe restored — wiped ${wiped}, re-inserted ${inserted} sessions.`
      );
      setReseedOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reseed Chloe");
    } finally {
      setReseeding(false);
    }
  };

  const openReset = async () => {
    setResetConfirm("");
    setResetPreview(null);
    setResetOpen(true);
    try {
      const { data } = await api.get("/admin/reset-activity/preview");
      setResetPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load account counts");
    }
  };

  const handleResetActivity = async () => {
    setResetting(true);
    try {
      const { data } = await api.post("/admin/reset-activity", {
        confirm: resetConfirm.trim(),
      });
      const d = data?.deleted || {};
      toast.success(
        `Activity cleared for ${data?.email} — ${d.sessions ?? 0} sessions, ${d.invoices ?? 0} invoices, ${d.packages ?? 0} packs removed.`
      );
      setResetOpen(false);
      setResetConfirm("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reset account activity");
    } finally {
      setResetting(false);
    }
  };

  if (!isAdmin) {
    return (
      <div
        className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6"
        data-testid="admin-forbidden"
      >
        <div className="w-14 h-14 rounded-full bg-[#F9E4DF] border border-[#F0C9C1] flex items-center justify-center mb-5">
          <ShieldAlert className="w-7 h-7 text-[#A8372A]" />
        </div>
        <h1 className="font-display text-3xl font-black uppercase tracking-tight mb-2">
          Access denied
        </h1>
        <p className="text-[#6B635C] text-sm max-w-md mb-6">
          This area is restricted. Your account does not have admin privileges.
        </p>
        <Button
          onClick={() => navigate("/dashboard")}
          data-testid="admin-back-dashboard"
          className="bg-[#0F6B6B] text-[#FFFFFF] hover:bg-[#0F6B6B]/90"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-page">
      <PageBand
        overlap
        testid="admin-band"
        title={
          <div>
            <p style={{ fontSize: 12, fontWeight: 600 }} className="uppercase tracking-widest text-[#D3E8E6]">
              Admin Console
            </p>
            <h1 className="font-bold leading-tight mt-1" style={{ fontSize: 22, letterSpacing: "-0.02em" }}>
              All users
            </h1>
          </div>
        }
        right={
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <button
              onClick={() => setReseedOpen(true)}
              data-testid="admin-reseed-chloe-btn"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 h-10 px-3.5 rounded-full text-white text-sm font-semibold bg-white/15 backdrop-blur hover:bg-white/25 transition-colors whitespace-nowrap"
            >
              <RefreshCcw className="w-4 h-4 shrink-0" /> <span className="truncate">Restore Chloe demo</span>
            </button>
            <button
              onClick={openReset}
              data-testid="admin-reset-activity-btn"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 h-10 px-3.5 rounded-full text-white text-sm font-semibold bg-[#A8372A]/30 hover:bg-[#A8372A]/45 transition-colors whitespace-nowrap"
            >
              <Eraser className="w-4 h-4 shrink-0" /> <span className="truncate">Reset account activity</span>
            </button>
          </div>
        }
      >
        <div className="mt-3">
          <div style={{ fontSize: 38, fontWeight: 800, lineHeight: 1.05, letterSpacing: "-0.02em" }} data-testid="admin-total-users">
            {users?.length ?? "—"}
          </div>
          <div style={{ fontSize: 13, color: "#D3E8E6" }} className="mt-1">
            Total accounts · every trainer on TrainMate
          </div>
        </div>
      </PageBand>

      <p className="text-[#6B635C] text-sm">
        Every account that has signed up to TrainMate.
      </p>

      {error && (
        <div
          className="p-4 rounded-md bg-[#F9E4DF] border border-[#F0C9C1] text-[#A8372A] text-sm"
          data-testid="admin-error"
        >
          {error}
        </div>
      )}

      {!users && !error && (
        <div className="text-[#6B635C] text-sm" data-testid="admin-loading">
          Loading users…
        </div>
      )}

      {users && (
        <div
          className="rounded-lg border tm-divider bg-[#FFFFFF] overflow-hidden"
          data-testid="admin-users-table-wrapper"
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-users-table">
              <thead className="bg-[#FAF7F2] text-[#6B635C]">
                <tr>
                  <th className="text-left font-medium uppercase tracking-wider text-[11px] px-4 py-3">
                    Name
                  </th>
                  <th className="text-left font-medium uppercase tracking-wider text-[11px] px-4 py-3">
                    Email
                  </th>
                  <th className="text-left font-medium uppercase tracking-wider text-[11px] px-4 py-3">
                    Signed up
                  </th>
                  <th className="text-left font-medium uppercase tracking-wider text-[11px] px-4 py-3">
                    Last login
                  </th>
                  <th className="text-right font-medium uppercase tracking-wider text-[11px] px-4 py-3">
                    Clients
                  </th>
                  <th className="text-right font-medium uppercase tracking-wider text-[11px] px-4 py-3">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="text-center text-[#6B635C] py-10"
                      data-testid="admin-users-empty"
                    >
                      No users yet.
                    </td>
                  </tr>
                )}
                {users.map((u) => (
                  <tr
                    key={u.user_id}
                    className="border-t tm-divider hover:bg-[#F3EDE4] transition-colors"
                    data-testid={`admin-user-row-${u.user_id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {u.picture ? (
                          <img
                            src={u.picture}
                            alt=""
                            className="w-8 h-8 rounded-full border border-[#E8E1D6]"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-[#E8E1D6] flex items-center justify-center text-xs font-bold">
                            {(u.name || u.email || "U")[0].toUpperCase()}
                          </div>
                        )}
                        <span
                          className="font-medium"
                          data-testid={`admin-user-name-${u.user_id}`}
                        >
                          {u.name || "—"}
                        </span>
                      </div>
                    </td>
                    <td
                      className="px-4 py-3 text-[#2A2623]"
                      data-testid={`admin-user-email-${u.user_id}`}
                    >
                      {u.email}
                    </td>
                    <td
                      className="px-4 py-3 text-[#6B635C]"
                      data-testid={`admin-user-signup-${u.user_id}`}
                    >
                      {fmtDateShort(u.created_at)}
                    </td>
                    <td
                      className="px-4 py-3 text-[#6B635C]"
                      data-testid={`admin-user-lastlogin-${u.user_id}`}
                    >
                      {fmtDate(u.last_login_at)}
                    </td>
                    <td
                      className="px-4 py-3 text-right font-display font-black tabular-nums"
                      data-testid={`admin-user-clients-${u.user_id}`}
                    >
                      {u.client_count}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={
                          u.user_id === user.user_id ||
                          impersonatingId === u.user_id
                        }
                        onClick={() => handleImpersonate(u)}
                        data-testid={`admin-impersonate-${u.user_id}`}
                        className="border-[#0F6B6B]/40 text-[#0F6B6B] hover:bg-[#0F6B6B] hover:text-[#FFFFFF] disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Eye className="w-3.5 h-3.5 mr-1.5" />
                        {impersonateLabel(u, user.user_id, impersonatingId)}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <AlertDialog open={reseedOpen} onOpenChange={setReseedOpen}>
        <AlertDialogContent
          data-testid="admin-reseed-confirm-dialog"
          className="bg-[#FFFFFF] border tm-divider text-[#2A2623]"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase tracking-tight">
              Restore Chloe demo client?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#6B635C] text-sm space-y-2">
              <span className="block">
                This runs against <span className="font-semibold text-[#2A2623]">your currently signed-in account</span>
                {" "}— which is the one you should be logged into for the trainer whose Chloe needs restoring.
              </span>
              <span className="block">
                It will create Chloe Sinclair (if missing) and insert exactly{" "}
                <span className="font-semibold text-[#0F6B6B]">19 completed + 1 no-show</span>{" "}
                seed sessions tagged with a hidden marker. Re-runs wipe only those marker rows before re-inserting them, so any Chloe sessions you added manually are preserved.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="admin-reseed-cancel"
              disabled={reseeding}
              className="bg-transparent border tm-divider text-[#2A2623] hover:bg-[#FAF7F2]"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="admin-reseed-confirm"
              disabled={reseeding}
              onClick={(e) => {
                e.preventDefault();
                handleReseedChloe();
              }}
              className="bg-[#0F6B6B] text-[#FFFFFF] hover:bg-[#0F6B6B]/90"
            >
              {reseeding ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Restoring…
                </>
              ) : (
                <>
                  <RefreshCcw className="w-4 h-4 mr-2" />
                  Restore Chloe
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={resetOpen} onOpenChange={setResetOpen}>
        <AlertDialogContent
          data-testid="admin-reset-confirm-dialog"
          className="bg-[#FFFFFF] border tm-divider text-[#2A2623]"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase tracking-tight text-[#A8372A]">
              Reset account activity?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#6B635C] text-sm space-y-3">
              <span className="block">
                This runs on the account you are signed in as:{" "}
                <span
                  className="font-semibold text-[#2A2623]"
                  data-testid="admin-reset-account-email"
                >
                  {resetPreview?.email || user?.email}
                </span>
              </span>
              <span className="block" data-testid="admin-reset-counts">
                It will permanently delete{" "}
                <span className="font-semibold text-[#A8372A]">
                  {resetPreview?.sessions ?? "…"} sessions
                </span>
                ,{" "}
                <span className="font-semibold text-[#A8372A]">
                  {resetPreview?.invoices ?? "…"} invoices
                </span>
                , all assigned packs, recurring series and body-metric entries.
              </span>
              <span className="block">
                Your{" "}
                <span className="font-semibold text-[#2A2623]">
                  {resetPreview?.clients ?? "…"} client records
                </span>
                , settings (ABN, bank details, GST switch), package templates and
                invoice-number counter are kept.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <label
              htmlFor="admin-reset-input"
              className="text-xs uppercase tracking-wider text-[#6B635C]"
            >
              Type RESET to confirm
            </label>
            <Input
              id="admin-reset-input"
              data-testid="admin-reset-input"
              value={resetConfirm}
              onChange={(e) => setResetConfirm(e.target.value)}
              placeholder="RESET"
              autoComplete="off"
              className="bg-[#FAF7F2] border-[#E8E1D6] text-[#2A2623]"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="admin-reset-cancel"
              disabled={resetting}
              className="bg-transparent border tm-divider text-[#2A2623] hover:bg-[#FAF7F2]"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="admin-reset-confirm"
              disabled={resetting || resetConfirm.trim() !== "RESET"}
              onClick={(e) => {
                e.preventDefault();
                handleResetActivity();
              }}
              className="bg-[#A8372A] text-[#FFFFFF] hover:bg-[#8E2E23] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {resetting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Resetting…
                </>
              ) : (
                <>
                  <Eraser className="w-4 h-4 mr-2" />
                  Reset activity
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
